import { Typography } from "@mui/material";
import { PayloadAction } from "@reduxjs/toolkit";
import { ExternalLink } from "ExternalLink";
import { OLButton } from "OLButton";
import { useAppDispatch } from "app/hooks";
import { isJSON } from "components/FiltersV2/utils";
import { DragDropInput } from "components/FormControls/DragDropInput";
import { OLModal } from "components/Tooltips/OLModal";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./ImportJsonModal.styles";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  headerMessage: string;
  importTooBigErrorMessage: string;
  setJsonAction: (payload: any[]) => PayloadAction<any[]>;
  link: string;
  validate?: (imports: any[]) => string | null; // Return null or error msg
  filter?: (imports: any[]) => any[];
}

export const ImportJsonModal = ({
  isOpen,
  onClose,
  headerMessage,
  importTooBigErrorMessage,
  setJsonAction,
  link,
  validate,
  filter,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return (
    <OLModal
      header={message({ id: headerMessage })}
      open={isOpen}
      onClose={onClose}
    >
      <Formik
        initialValues={{ import: undefined }}
        onSubmit={async (values, { setFieldError }) => {
          if (!values.import) return;
          const fileReader = new FileReader();

          fileReader.onload = (e) => {
            if (e.target?.result) {
              const isValidJson = isJSON(e.target.result);
              if (!isValidJson) {
                return setFieldError(
                  "import",
                  message({ id: "invalidJsonFile" }),
                );
              }

              const json = JSON.parse(e.target.result as string);
              const maxImportCount = 50;
              const filteredData = filter ? filter(json) : json;
              const isImportTooBig = filteredData.length > maxImportCount;

              if (isImportTooBig) {
                return setFieldError(
                  "import",
                  message({ id: importTooBigErrorMessage }),
                );
              }

              if (validate) {
                const isValid = validate(filteredData);

                if (isValid)
                  return setFieldError("import", message({ id: isValid }));
              }

              dispatch(setJsonAction(filteredData));

              return navigate(link);
            }
          };

          fileReader.readAsText(values.import[0]);
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, errors, values }) => {
          const isError = errors?.import;

          return (
            <Form onSubmit={handleSubmit}>
              <DragDropInput
                data-test-id="import-json-modal"
                description={
                  <div>
                    <Typography className={classes.description}>
                      <Typography className={classes.descPurple}>
                        {message({ id: "clickToUpload" })}{" "}
                      </Typography>
                      <Typography fontSize={16}>
                        {message({ id: "orDragAndDropFile" })}
                      </Typography>
                    </Typography>
                    <Typography className={classes.supportedFiles}>
                      <span>
                        {message(
                          { id: "jsonFileMaxSize" },
                          {
                            size: 100,
                          },
                        )}
                      </span>
                    </Typography>
                  </div>
                }
                header={message({
                  id: "uploadFile",
                })}
                singleFile
                onChange={(acceptedFiles, rejectedFiles) => {
                  setFieldValue(
                    "import",
                    acceptedFiles.length > 0
                      ? [acceptedFiles[0]]
                      : [rejectedFiles[0].file],
                  );
                }}
                onDelete={() => setFieldValue("import", undefined)}
                value={values.import}
                accept={{ "application/json": [".json"] }}
              />
              {isError && (
                <span className={classes.error}>{errors.import}</span>
              )}
              <Typography className={classes.linksText}>
                {message(
                  { id: "readImportGuide" },
                  {
                    importGuide: (
                      <ExternalLink path="https://help.openloyalty.io/imports-exports/imports">
                        {message({
                          id: "importGuide",
                        }).toLowerCase()}
                      </ExternalLink>
                    ),
                  },
                )}
              </Typography>
              <OLButton
                color="primary"
                disabled={isSubmitting || !values.import}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="import"
              >
                {message({ id: "import" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
